import { keyframes } from '@emotion/react';
import styled from '@emotion/styled';
import React, { MutableRefObject, ReactNode, useLayoutEffect, useMemo, useRef, useState } from 'react';

/**
 * Анимация появления и закрытия календаря.
 *
 * react-datepicker при закрытии просто убирает поповер из дерева — анимации закрытия у него нет.
 * Но обёртку `popperContainer` он рендерит всегда, и закрытому календарю передаёт её без детей.
 * Слой ниже держит последнее содержимое, пока играет анимация закрытия, и только потом отпускает.
 *
 * Длительности и кривые — как в Material 3: появление — «emphasized decelerate», закрытие короче
 * и с ускорением (уходящее не должно задерживать взгляд). В теме осей анимации нет, поэтому значения
 * живут здесь, рядом с единственным потребителем.
 */

/** Появление: 180 мс — в окне 100–250 мс, где отклик на клик ощущается мгновенным, но заметным. */
export const CALENDAR_ENTER_MS = 180;
/** Закрытие короче появления — уходящее не задерживает взгляд. */
export const CALENDAR_EXIT_MS = 120;

const EASE_ENTER = 'cubic-bezier(0.05, 0.7, 0.1, 1)';
const EASE_EXIT = 'cubic-bezier(0.3, 0, 0.8, 0.15)';

/**
 * `pop` — обычное появление: проявляется и чуть «выезжает» от поля.
 * `fade` — только проявление на месте: календарь конца диапазона встаёт поверх календаря начала,
 * и сдвиг выглядел бы как прыжок.
 */
export type CalendarEnter = 'pop' | 'fade';

/**
 * `pop` — обычное закрытие. `hold` — календарь остаётся неподвижным, пока поверх него проявляется
 * календарь конца диапазона: так переход «С» → «По» читается как плавное перекрашивание одного
 * календаря, а не как закрытие одного и открытие другого.
 */
export type CalendarExit = 'pop' | 'hold';

export interface CalendarMotion {
  enter: CalendarEnter;
  exit: CalendarExit;
}

export const DEFAULT_CALENDAR_MOTION: CalendarMotion = { enter: 'pop', exit: 'pop' };

const popIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(var(--ud-calendar-shift)) scale(0.98);
  }
  to {
    opacity: 1;
    transform: none;
  }
`;

const popOut = keyframes`
  from {
    opacity: 1;
    transform: none;
  }
  to {
    opacity: 0;
    transform: translateY(var(--ud-calendar-shift)) scale(0.98);
  }
`;

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const fadeOut = keyframes`
  from { opacity: 1; }
  to { opacity: 0; }
`;

/**
 * `display: contents` — слой не участвует в раскладке: поповер позиционируется так же, как без него.
 * Анимируется сам календарь (`.react-datepicker`), а не поповер: у поповера `transform` занят Popper-ом.
 */
const Layer = styled.div`
  display: contents;

  .react-datepicker-popper {
    --ud-calendar-shift: -4px;
  }

  .react-datepicker-popper[data-placement^='top'] {
    --ud-calendar-shift: 4px;
  }

  .react-datepicker-popper[data-placement^='bottom'] .react-datepicker {
    transform-origin: top left;
  }

  .react-datepicker-popper[data-placement^='top'] .react-datepicker {
    transform-origin: bottom left;
  }

  .react-datepicker {
    animation: ${popIn} ${CALENDAR_ENTER_MS}ms ${EASE_ENTER} both;
  }

  &[data-enter='fade'] .react-datepicker {
    animation-name: ${fadeIn};
  }

  &[data-leaving] .react-datepicker {
    pointer-events: none;
  }

  &[data-leaving='pop'] .react-datepicker {
    animation: ${popOut} ${CALENDAR_EXIT_MS}ms ${EASE_EXIT} both;
  }

  &[data-leaving='hold'] .react-datepicker {
    animation: none;
  }

  /* WCAG 2.3.3: без смещения и масштаба — только проявление, оно не укачивает. */
  @media (prefers-reduced-motion: reduce) {
    .react-datepicker {
      animation-name: ${fadeIn};
    }

    &[data-leaving='pop'] .react-datepicker {
      animation-name: ${fadeOut};
    }
  }
`;

function createCalendarLayer(motionRef: MutableRefObject<CalendarMotion>) {
  return function CalendarLayer({ children }: { children?: ReactNode }) {
    const isOpen = React.Children.count(children) > 0;
    const keptRef = useRef<ReactNode>(null);
    const wasOpenRef = useRef(false);
    const enterRef = useRef<CalendarEnter>(motionRef.current.enter);
    const [leaving, setLeaving] = useState<CalendarExit | null>(null);
    const [, release] = useState(0);

    // Способ появления фиксируется в момент открытия: дальше пропсы могут смениться, а анимация — нет.
    if (isOpen && !wasOpenRef.current) enterRef.current = motionRef.current.enter;
    if (isOpen) keptRef.current = children;

    useLayoutEffect(() => {
      const wasOpen = wasOpenRef.current;
      wasOpenRef.current = isOpen;

      if (isOpen) {
        setLeaving(null);
        return undefined;
      }
      if (!wasOpen || !keptRef.current) return undefined;

      const exit = motionRef.current.exit;
      setLeaving(exit);
      const timer = setTimeout(
        () => {
          keptRef.current = null;
          setLeaving(null);
          release((n) => n + 1);
        },
        exit === 'hold' ? CALENDAR_ENTER_MS : CALENDAR_EXIT_MS,
      );
      return () => clearTimeout(timer);
    }, [isOpen]);

    const content = isOpen ? children : keptRef.current;
    if (!content) return null;

    return (
      <Layer data-enter={enterRef.current} data-leaving={isOpen ? undefined : (leaving ?? undefined)}>
        {content}
      </Layer>
    );
  };
}

/**
 * Обёртка поповера для react-datepicker (`popperContainer`). Компонент создаётся один раз на поле:
 * новый тип на каждый рендер размонтировал бы календарь. Способ появления и закрытия читается из
 * ссылки в момент перехода, поэтому его можно менять пропсами на лету.
 */
export function useCalendarLayer(motion?: Partial<CalendarMotion>) {
  const motionRef = useRef<CalendarMotion>(DEFAULT_CALENDAR_MOTION);
  motionRef.current = { ...DEFAULT_CALENDAR_MOTION, ...motion };

  return useMemo(() => createCalendarLayer(motionRef), []);
}
